import { createFileRoute } from "@tanstack/react-router";
import { ContentPage } from "@/components/ContentPage";

import { CallbackRail } from "@/components/CallbackRail";
import { FaqSection, faqJsonLd, type FaqItem } from "@/components/FaqSection";
import { IMAGES, BBN_SECURITY } from "@/lib/images";

const PDF = "/datablad/3m-safety-s140.pdf";

const FAQS: FaqItem[] = [
  {
    q: "När passar klar säkerhetsfilm bäst?",
    a: "Klar säkerhetsfilm passar när glaset redan har solskydd genom energiglas eller lågemissionsglas, eller när dekorfolie ger insynsskydd. Den passar också när fasaden ska se orörd ut, till exempel på kulturhistoriska byggnader och butiker med helt klara skyltfönster.",
  },
  {
    q: "Försämrar klar film glasets prestanda?",
    a: "Påverkan är knappt märkbar, med >88 % visuell ljustransmission och minimal spegelreflektans. Glaset ser orört ut från båda sidor och beter sig som säkerhetsglas vid slag och splitter.",
  },
  {
    q: "Kan klar säkerhetsfilm kombineras med befintlig solfilm?",
    a: "Ja. Den klara säkerhetsfilmen monteras invändigt direkt mot glaset. Solfilmen sitter på utsidan (eller på insidan av andra glaset i isolerrutan). Båda funktionerna behålls. Vi kontrollerar alltid att befintlig film sitter fast innan installation.",
  },
  {
    q: "Prisskillnad mot tonad säkerhetsfilm?",
    a: "Klar säkerhetsfilm är typiskt 10–15 % billigare per m² än motsvarande tonad version, eftersom den saknar spektralt selektivt skikt. Skyddet är likvärdigt.",
  },
];

export const Route = createFileRoute("/3m-sakerhetsfilm-utan-solfilm")({
  head: () => ({
    meta: [
      { title: "3M säkerhetsfilm utan solfilm — klar säkerhetsfilm | BBN Nordic" },
      { name: "description", content: "Klar 3M-säkerhetsfilm — samma skydd mot inbrott och splitter, men helt optiskt osynlig. Perfekt för butiksfönster och kulturhistoriska fasader." },
      { property: "og:title", content: "3M säkerhetsfilm utan solfilm" },
      { property: "og:description", content: "Klar säkerhetsfilm — optiskt osynlig, samma styrka som tonad." },
      { property: "og:type", content: "product" },
      { property: "og:url", content: "https://bbnnordic.se/3m-sakerhetsfilm-utan-solfilm" },
      { property: "og:image", content: IMAGES.sakerhetsfilm },
    ],
    links: [{ rel: "canonical", href: "https://bbnnordic.se/3m-sakerhetsfilm-utan-solfilm" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(faqJsonLd(FAQS)) },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Product",
          name: "3M klar säkerhetsfilm",
          category: "Fönsterfilm / Klar säkerhetsfilm",
          description:
            "Klar 3M-säkerhetsfilm — samma skydd mot inbrott och splitter, men optiskt osynlig. Ljustransmission >88 %. Perfekt för butiksfönster och kulturhistoriska fasader.",
          image: IMAGES.sakerhetsfilm,
          brand: { "@type": "Brand", name: "3M" },
          manufacturer: { "@type": "Organization", name: "3M" },
          material: "Polyesterlaminat, klar",
          url: "https://bbnnordic.se/3m-sakerhetsfilm-utan-solfilm",
          additionalProperty: [
            { "@type": "PropertyValue", name: "Ljustransmission (VLT)", value: ">88 %", unitText: "%" },
            { "@type": "PropertyValue", name: "Tjocklek", value: "100 / 175 / 300 μm", unitText: "μm" },
            { "@type": "PropertyValue", name: "Standard", value: "EN 12600 säkerhetsklass" },
            { "@type": "PropertyValue", name: "Garanti", value: "10 år produktgaranti + 10 år arbetsgaranti" },
          ],
          offers: {
            "@type": "AggregateOffer",
            priceCurrency: "SEK",
            lowPrice: "950",
            priceSpecification: { "@type": "PriceSpecification", price: "950", priceCurrency: "SEK" },
            price: "950",
            highPrice: "2200",
            availability: "https://schema.org/InStock",
            priceValidUntil: "2026-12-31",
            seller: { "@id": "https://bbnnordic.se/#organization" },
            areaServed: "SE",
            hasMerchantReturnPolicy: {
              "@type": "MerchantReturnPolicy",
              applicableCountry: "SE",
              returnPolicyCategory: "https://schema.org/MerchantReturnNotPermitted",
            },
            shippingDetails: {
              "@type": "OfferShippingDetails",
              shippingRate: { "@type": "MonetaryAmount", value: "0", currency: "SEK" },
              shippingDestination: { "@type": "DefinedRegion", addressCountry: "SE" },
              deliveryTime: {
                "@type": "ShippingDeliveryTime",
                handlingTime: { "@type": "QuantitativeValue", minValue: 0, maxValue: 5, unitCode: "DAY" },
                transitTime: { "@type": "QuantitativeValue", minValue: 1, maxValue: 14, unitCode: "DAY" },
              },
            },
          },
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: "4.8",
            reviewCount: "6",
            bestRating: "5",
            worstRating: "1",
          },
          review: [
            {
              "@type": "Review",
              reviewRating: { "@type": "Rating", ratingValue: "5", bestRating: "5" },
              author: { "@type": "Person", name: "Andrée Johnsson" },
              reviewBody: "Kan varmt rekommenderas. Vi hade en undermålig solfilm som konkurrent satt upp, Marcus knackade på när han var hos grannen och sa att det där kan göras bättre.",
            },
            {
              "@type": "Review",
              reviewRating: { "@type": "Rating", ratingValue: "5", bestRating: "5" },
              author: { "@type": "Person", name: "Christian Backe" },
              reviewBody: "Vi hade problem med att våra fönster släppte in väldigt mycket värme och valde en film som BBN satte med bästa resultat. Kan varmt rekommendera!",
            },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Hem", item: "https://bbnnordic.se/" },
            { "@type": "ListItem", position: 2, name: "Säkerhetsfilm", item: "https://bbnnordic.se/sakerhetsfilm" },
            { "@type": "ListItem", position: 3, name: "Utan solfilm", item: "https://bbnnordic.se/3m-sakerhetsfilm-utan-solfilm" },
          ],
        }),
      },
    ],
  }),
  component: () => (
    <ContentPage
      eyebrow="KLAR SÄKERHETSFILM"
      title="3M säkerhetsfilm utan solfilm"
      subtitle="Klar säkerhetsfilm bevarar glasets utseende och får det att bete sig som säkerhetsglas vid slag och splitter."
      heroImage={BBN_SECURITY[3]}
      breadcrumbs={[{ label: "Säkerhetsfilm", to: "/sakerhetsfilm" }, { label: "Utan solfilm" }]}
      sections={[
        {
          eyebrow: "OPTIK",
          heading: "Klar säkerhetsfilm släpper igenom >88 % av ljuset.",
          paragraphs: [
            "Klar säkerhetsfilm saknar spektralt selektivt skikt. Den släpper igenom >88 % av det synliga ljuset och har minimal spegelreflektans. Den som ser fönstret utifrån ser inte filmen.",
            "Det passar butiksfönster där varorna ska synas tydligt, fasader med kulturhistoriska värden och glas som redan har inbyggt solskydd.",
          ],
          bullets: [
            "Ingen färgförändring av glaset",
            ">88 % visuell ljustransmission",
            "Passar butiksmonter, vitriner och skyltfönster",
            "Lämpligt när fasadens uttryck ska bevaras helt orört",
          ],
          image: BBN_SECURITY[6],
        },
        {
          eyebrow: "SORTIMENT",
          heading: "3M Safety S140 — en av 3M:s kraftigaste klara filmer.",
          bullets: [
            "355 μm (14 mil) — kraftig inbrottsfördröjning",
            "EN 12600 pendelslag klass 1B1",
            "Kompletteras med Ultra S600/S800 för högsta klassen",
            "10 års produktgaranti + 10 års arbetsgaranti",
          ],
          extra: (
            <a
              href={PDF}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-navy text-cream px-5 py-3 text-sm font-semibold hover:bg-navy/90 transition-colors"
            >
              Datablad Safety S140 (PDF) →
            </a>
          ),
          image: IMAGES.installer,
        },
        {
          eyebrow: "TYPISK ANVÄNDNING",
          heading: "Där glasets utseende ska bevaras.",
          bullets: [
            "Butiksskyltfönster — produkter ska synas utan färgförskjutning",
            "Kulturhistoriska byggnader och kyrkor",
            "Livsmedelsbutiker och storkök — hygien och synlig produkt",
            "Ambassader och myndighetsbyggnader med bevarad fasad",
            "Glas som redan har lågemissions- eller energiglas",
          ],
        },
      ]}
      related={[
        { to: "/3m-ultra-sakerhetsfilm", label: "3M Ultra", desc: "När maximalt inbrottsskydd krävs." },
        { to: "/3m-sakerhetsfilm-med-solfilm", label: "Kombifilm", desc: "Säkerhet + solreduktion." },
        { to: "/sakerhetsfilm", label: "Säkerhetsfilm — översikt", desc: "Se de olika säkerhetsfilmerna." },
      ]}
    >
      <FaqSection heading="Vanliga frågor om klar säkerhetsfilm." items={FAQS} />
      
      <CallbackRail product="Säkerhetsfilm" />
    </ContentPage>
  ),
});
